// components/DepositosReserva.js
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Table,
  Button,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Card,
  Statistic,
  Divider,
  Popconfirm,
  Badge,
  Alert,
  Spin
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  CreditCardOutlined,
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  SaveOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option, OptGroup } = Select;

const DepositosReserva = ({ 
  numReserva, 
  nombreCliente, 
  visible, 
  onClose,
  onDepositoCreado 
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingLineas, setLoadingLineas] = useState(false);
  const [depositos, setDepositos] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [lineasReserva, setLineasReserva] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editandoDeposito, setEditandoDeposito] = useState(null);
  const [form] = Form.useForm();

  // Cargar depósitos, formas de pago y líneas al abrir el modal
  useEffect(() => {
    if (visible && numReserva) {
      fetchDepositos();
      fetchFormasPago();
      fetchLineasReserva();
    }
  }, [visible, numReserva]);

  const fetchDepositos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/depositos/reservas/${numReserva}/depositos`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDepositos(response.data.data || []);
    } catch (error) {
      console.error('Error cargando depósitos:', error);
      message.error('Error al cargar los depósitos');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormasPago = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/formaspago/dropdown/mostrar`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setFormasPago(response.data.data || []);
    } catch (error) {
      console.error('Error cargando formas de pago:', error);
      message.error('Error al cargar formas de pago');
    }
  };

  const fetchLineasReserva = async () => {
    setLoadingLineas(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/reservas/${numReserva}/lineas`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Ordenar líneas por número de línea y ocupante
      const lineasOrdenadas = (response.data.data || [])
        .sort((a, b) => {
          if (a.Linea === b.Linea) {
            return a.Numocupante - b.Numocupante;
          }
          return a.Linea - b.Linea;
        });
      
      setLineasReserva(lineasOrdenadas);
    } catch (error) {
      console.error('Error cargando líneas de reserva:', error);
      message.error('Error al cargar las líneas de reserva');
    } finally {
      setLoadingLineas(false);
    }
  };

  // Agrupar líneas por número de habitación
  const agruparLineasPorHabitacion = () => {
    const lineasAgrupadas = {};
    
    lineasReserva.forEach(linea => {
      const habitacion = linea.NumHab || `Habitación ${linea.Linea}`;
      
      if (!lineasAgrupadas[habitacion]) {
        lineasAgrupadas[habitacion] = {
          habitacion: habitacion,
          tipoHab: linea.TipoHab,
          lineas: []
        };
      }
      
      lineasAgrupadas[habitacion].lineas.push({
        linea: linea.Linea,
        ocupante: linea.Numocupante,
        nombre: linea.Nombre,
        tipoFac: linea.TipoFac
      });
    });
    
    return Object.values(lineasAgrupadas);
  };

  const handleNuevoDeposito = () => {
    setEditandoDeposito(null);
    form.resetFields();
    
    // Obtener primera línea por defecto
    const primeraLinea = lineasReserva.length > 0 ? lineasReserva[0] : null;
    
    // Establecer valores por defecto
    form.setFieldsValue({
      fecha: moment(),
      importe: 0,
      FPago: '',
      Observaciones: '',
      Linea: primeraLinea ? primeraLinea.Linea : 1,
      Ocupante: primeraLinea ? primeraLinea.Numocupante : 1,
      NumHab: primeraLinea ? primeraLinea.NumHab : null
    });
    
    setModalVisible(true);
  };

  const handleEditarDeposito = (deposito) => {
    setEditandoDeposito(deposito);
    form.setFieldsValue({
      ...deposito,
      fecha: deposito.fecha ? moment(deposito.fecha) : moment(),
      FPago: deposito.FPago || '',
      Observaciones: deposito.Observaciones || '',
      Linea: deposito.Linea || 1,
      Ocupante: deposito.Ocupante || 1,
      NumHab: deposito.NumHab || null
    });
    setModalVisible(true);
  };

  // Cuando cambia la línea, actualizar el número de habitación
  const handleCambioLinea = (lineaSeleccionada) => {
    if (lineaSeleccionada) {
      // Buscar la línea seleccionada
      const linea = lineasReserva.find(l => 
        l.Linea === parseInt(lineaSeleccionada.split('-')[0]) && 
        l.Numocupante === parseInt(lineaSeleccionada.split('-')[1])
      );
      
      if (linea) {
        form.setFieldsValue({
          NumHab: linea.NumHab
        });
      }
    }
  };

  // Obtener opciones para el select de línea/ocupante
  const getOpcionesLineaOcupante = () => {
    if (lineasReserva.length === 0) {
      return [
        <Option key="1-1" value="1-1">
          Línea 1 - Ocupante 1
        </Option>
      ];
    }

    // Agrupar por habitación
    const lineasAgrupadas = agruparLineasPorHabitacion();
    
    return lineasAgrupadas.map(grupo => (
      <OptGroup 
        key={grupo.habitacion} 
        label={`${grupo.habitacion} (${grupo.tipoHab})`}
      >
        {grupo.lineas.map(item => (
          <Option 
            key={`${item.linea}-${item.ocupante}`} 
            value={`${item.linea}-${item.ocupante}`}
          >
            Línea {item.linea} - Ocupante {item.ocupante} ({item.nombre})
          </Option>
        ))}
      </OptGroup>
    ));
  };

  const handleGuardarDeposito = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // Separar línea y ocupante del valor del select
      const [linea, ocupante] = values.lineaOcupante ? 
        values.lineaOcupante.split('-').map(Number) : [1, 1];
      
      const token = localStorage.getItem('token');
      const datosDeposito = {
        ...values,
        NumReserva: numReserva,
        Nombre: nombreCliente,
        fecha: values.fecha.format('YYYY-MM-DD'),
        importe: parseFloat(values.importe),
        Linea: linea,
        Ocupante: ocupante,
        // Obtener número de habitación de la línea seleccionada
        NumHab: values.NumHab || null,
        Usuario: 'ADMIN', // En producción, obtener del usuario autenticado
        CodigoHotel: 77, // Debería venir del contexto
        SerieFactura: 'Z', // Por defecto
        Contabilizado: false
      };

      if (editandoDeposito) {
        // Actualizar depósito existente
        await axios.put(
          `${BASE_URL}/api/depositos/${editandoDeposito.i}`,
          datosDeposito,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Depósito actualizado correctamente');
      } else {
        // Crear nuevo depósito
        await axios.post(
          `${BASE_URL}/api/depositos`,
          datosDeposito,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Depósito creado correctamente');
      }

      // Recargar lista de depósitos
      fetchDepositos();
      setModalVisible(false);
      
      // Notificar al componente padre si es necesario
      if (onDepositoCreado) {
        onDepositoCreado();
      }
      
    } catch (error) {
      console.error('Error guardando depósito:', error);
      message.error(error.response?.data?.error || 'Error al guardar el depósito');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarDeposito = async (depositoId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${BASE_URL}/api/depositos/${depositoId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      message.success('Depósito eliminado correctamente');
      fetchDepositos();
      
    } catch (error) {
      console.error('Error eliminando depósito:', error);
      message.error(error.response?.data?.error || 'Error al eliminar el depósito');
    } finally {
      setLoading(false);
    }
  };

  const calcularTotalDepositos = () => {
    return depositos.reduce((total, deposito) => total + parseFloat(deposito.importe || 0), 0);
  };

  const columns = [
    // {
    //   title: 'ID',
    //   dataIndex: 'i',
    //   key: 'i',
    //   width: 80,
    //   render: (id) => <Tag color="blue">#{id}</Tag>,
    // },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 120,
      render: (fecha) => fecha ? moment(fecha).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Hora',
      key: 'hora',
      width: 100,
      render: (_, record) => {
        const hora = record.Hora || record.HoraM;
        return hora ? <Tag>{hora}</Tag> : '-';
      },
    },
    {
      title: 'Forma Pago',
      dataIndex: 'FPago',
      key: 'FPago',
      width: 120,
      render: (fpago) => {
        const formaPago = formasPago.find(fp => fp.value === fpago);
        return formaPago ? (
          <Tag color={fpago === 'EFE' ? 'green' : 'blue'}>
            {formaPago.label.split(' - ')[1] || fpago}
          </Tag>
        ) : <Tag>{fpago}</Tag>;
      },
    },
    {
      title: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 120,
      render: (importe) => (
        <Text strong style={{ color: '#52c41a' }}>
          {parseFloat(importe).toFixed(2)} €
        </Text>
      ),
    },
    {
      title: 'Línea/Ocupante',
      key: 'lineaOcupante',
      width: 120,
      render: (_, record) => (
        <Tag>
          L{record.Linea}-O{record.Ocupante}
        </Tag>
      ),
    },
    {
      title: 'Habitación',
      dataIndex: 'NumHab',
      key: 'NumHab',
      width: 100,
      render: (numHab) => numHab ? <Tag color="blue">{numHab}</Tag> : '-',
    },
    // {
    //   title: 'Estado',
    //   key: 'estado',
    //   width: 100,
    //   render: (_, record) => {
    //     if (record.Contabilizado) {
    //       return <Badge status="success" text="Contabilizado" />;
    //     } else if (record.nFactura > 0) {
    //       return <Badge status="processing" text="Facturado" />;
    //     } else {
    //       return <Badge status="default" text="Pendiente" />;
    //     }
    //   },
    // },
    {
      title: 'Observaciones',
      dataIndex: 'Observaciones',
      key: 'Observaciones',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="link"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditarDeposito(record)}
              disabled={record.Contabilizado || record.nFactura > 0}
            />
          </Tooltip>
          
          <Tooltip title="Eliminar">
            <Popconfirm
              title="¿Eliminar este depósito?"
              description="Esta acción no se puede deshacer"
              onConfirm={() => handleEliminarDeposito(record.i)}
              okText="Sí"
              cancelText="No"
              okButtonProps={{ danger: true }}
              disabled={record.Contabilizado || record.nFactura > 0}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                disabled={record.Contabilizado || record.nFactura > 0}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      {/* Modal principal de depósitos */}
      <Modal
        title={
          <Space>
            <CreditCardOutlined />
            <span>Depósitos - Reserva #{numReserva}</span>
            <Tag color="blue">{nombreCliente}</Tag>
          </Space>
        }
        open={visible}
        onCancel={onClose}
        width={1200}
        footer={[
          <Button key="close" onClick={onClose}>
            Cerrar
          </Button>
        ]}
      >
        {/* Resumen de depósitos */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Statistic
              title="Total Depósitos"
              value={calcularTotalDepositos()}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
              suffix="€"
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Número de Depósitos"
              value={depositos.length}
              prefix={<CreditCardOutlined />}
            />
          </Col>
        </Row>

        {/* Botón para añadir depósito */}
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleNuevoDeposito}
            disabled={loadingLineas}
          >
            Añadir Depósito
          </Button>
          
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchDepositos();
              fetchLineasReserva();
            }}
            style={{ marginLeft: 8 }}
            loading={loading || loadingLineas}
          >
            Actualizar
          </Button>

        </div>

        {/* Tabla de depósitos */}
        <Table
          columns={columns}
          dataSource={depositos}
          rowKey="i"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1000 }}
          size="small"
          summary={() => (
            depositos.length > 0 && (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                  <Table.Summary.Cell index={0} colSpan={5}>
                    <Text strong>Total:</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <Text strong style={{ color: '#52c41a' }}>
                      {calcularTotalDepositos().toFixed(2)} €
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} colSpan={4} />
                </Table.Summary.Row>
              </Table.Summary>
            )
          )}
        />

        {/* Alerta si no hay depósitos */}
        {depositos.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <CreditCardOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
            <Title level={4} style={{ color: '#bfbfbf' }}>
              No hay depósitos registrados
            </Title>
            <Text type="secondary">
              Puedes añadir un depósito haciendo clic en "Añadir Depósito"
            </Text>
          </div>
        )}
      </Modal>

      {/* Modal para crear/editar depósito */}
      <Modal
        title={editandoDeposito ? 'Editar Depósito' : 'Nuevo Depósito'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            Cancelar
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleGuardarDeposito}
            icon={<SaveOutlined />}
          >
            {editandoDeposito ? 'Actualizar' : 'Guardar'}
          </Button>
        ]}
      >
        {loadingLineas ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            <div style={{ marginTop: 16 }}>Cargando líneas de la reserva...</div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            disabled={editandoDeposito && (editandoDeposito.Contabilizado || editandoDeposito.nFactura > 0)}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="fecha"
                  label="Fecha"
                  rules={[{ required: true, message: 'La fecha es obligatoria' }]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    disabledDate={(current) => current && current > moment().endOf('day')}
                  />
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item
                  name="FPago"
                  label="Forma de Pago"
                  rules={[{ required: true, message: 'La forma de pago es obligatoria' }]}
                >
                  <Select
                    placeholder="Seleccione forma de pago"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {formasPago.map(fp => (
                      <Option key={fp.value} value={fp.value}>
                        {fp.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="importe"
                  label="Importe (€)"
                  rules={[
                    { required: true, message: 'El importe es obligatorio' },
                    { type: 'number', min: 0.01, message: 'El importe debe ser mayor a 0' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0.01}
                    step={0.01}
                    precision={2}
                    formatter={value => `${value} €`}
                    parser={value => value.replace(' €', '')}
                  />
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item
                  name="lineaOcupante"
                  label="Línea y Ocupante"
                  rules={[{ required: true, message: 'Debe seleccionar línea y ocupante' }]}
                >
                  <Select
                    placeholder="Seleccione línea y ocupante"
                    onChange={handleCambioLinea}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {getOpcionesLineaOcupante()}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="NumHab"
                  label="Habitación Asignada"
                >
                  <Input
                    disabled
                    placeholder="Se asignará automáticamente"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="Observaciones"
              label="Observaciones"
            >
              <Input.TextArea
                rows={3}
                placeholder="Observaciones adicionales del depósito..."
                maxLength={500}
              />
            </Form.Item>

            {editandoDeposito && (editandoDeposito.Contabilizado || editandoDeposito.nFactura > 0) && (
              <Alert
                message={
                  editandoDeposito.Contabilizado 
                    ? "Este depósito ya está contabilizado y no se puede modificar"
                    : "Este depósito está facturado y no se puede modificar"
                }
                type="warning"
                showIcon
                icon={<WarningOutlined />}
              />
            )}

            {/* Mostrar información de la línea seleccionada */}
            {form.getFieldValue('lineaOcupante') && (
              <Card size="small" style={{ marginTop: 16 }}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  <strong>Información de la línea seleccionada:</strong>
                  <div>
                    {(() => {
                      const [linea, ocupante] = form.getFieldValue('lineaOcupante').split('-');
                      const lineaInfo = lineasReserva.find(l => 
                        l.Linea === parseInt(linea) && 
                        l.Numocupante === parseInt(ocupante)
                      );
                      
                      if (lineaInfo) {
                        return (
                          <>
                            <div><strong>Nombre:</strong> {lineaInfo.Nombre}</div>
                            <div><strong>Tipo Habitación:</strong> {lineaInfo.TipoHab}</div>
                            <div><strong>Tipo Facturación:</strong> {lineaInfo.TipoFac}</div>
                            <div><strong>Regimen:</strong> {lineaInfo.Regimen}</div>
                          </>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              </Card>
            )}
          </Form>
        )}
      </Modal>
    </>
  );
};

export default DepositosReserva;